import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip
} from '@mui/material';
import {
  Translate,
  ArrowBack,
  SwapHoriz,
  ContentCopy,
  PostAdd
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

export const TranslationPage: React.FC = () => {
  const navigate = useNavigate();
  const [sourceText, setSourceText] = useState('');
  const [targetLang, setTargetLang] = useState('es');
  const [translating, setTranslating] = useState(false);
  const [translatedText, setTranslatedText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const languages = [
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'it', name: 'Italian' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'zh', name: 'Chinese' },
    { code: 'ja', name: 'Japanese' },
    { code: 'ko', name: 'Korean' }
  ];

  const translateText = async () => {
    if (!sourceText.trim()) return;

    setTranslating(true);
    setError(null);

    try {
      // Mock translation for now
      setTimeout(() => {
        setTranslatedText(`[Translated to ${languages.find(l => l.code === targetLang)?.name}] ${sourceText}`);
        setTranslating(false);
      }, 1500);
    } catch (err: any) {
      setError('Failed to translate text');
      setTranslating(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            Back to Dashboard
          </Button>
          <Translate color="primary" sx={{ mr: 2, fontSize: 32 }} />
          <Typography variant="h4" component="h1">
            Translation
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Translate your content to reach global audiences
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Source Text
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={8}
                value={sourceText}
                onChange={(e) => setSourceText(e.target.value)}
                placeholder="Enter text to translate..."
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControl sx={{ minWidth: 120 }}>
                  <InputLabel>To Language</InputLabel>
                  <Select
                    value={targetLang}
                    onChange={(e) => setTargetLang(e.target.value)}
                    label="To Language"
                  >
                    {languages.map((lang) => (
                      <MenuItem key={lang.code} value={lang.code}>
                        {lang.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  startIcon={translating ? <CircularProgress size={16} /> : <Translate />}
                  onClick={translateText}
                  disabled={!sourceText.trim() || translating}
                >
                  {translating ? 'Translating...' : 'Translate'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Translation
              </Typography>
              {translatedText ? (
                <Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={8}
                    value={translatedText}
                    onChange={(e) => setTranslatedText(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<ContentCopy />}
                      onClick={() => navigator.clipboard.writeText(translatedText)}
                    >
                      Copy
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<PostAdd />}
                      onClick={() => navigate('/create-post', {
                        state: {
                          topic: 'Translated content',
                          content: translatedText
                        }
                      })}
                    >
                      Create Post
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Typography color="text.secondary">
                  Translation will appear here
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default TranslationPage; 